import { useState } from "react";
import { useEffect } from "react";
import { Fragment } from "react";
import { connect } from "react-redux";

import EditIcon from "@mui/icons-material/Edit";
import BackIcon from "@mui/icons-material/NavigateBefore";
import NextIcon from "@mui/icons-material/NavigateNext";
import TrendingDown from "@mui/icons-material/TrendingDown";
import TrendingFlat from "@mui/icons-material/TrendingFlat";
import TrendingUp from "@mui/icons-material/TrendingUp";
import { Box } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { alpha, darken, lighten, useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import * as PatientSessionActions from "../../../state/redux/patientSession.js";
import { capitalizeAllWords, deepCopyObject, OmniStringToDate } from "../../../utils/helpers.js";
import PatientSymptomsDialog from "../../dialogs/demoPatientSymptomsDialog.js";
import SingleSymptomDialog from "../../dialogs/singleSymptomDialog.js";
import { extractGraphDataFromArray } from "../../graphing/graphUtils.js";
import OmniAvatar from "../../ui/omniAvatar.js";
import OmniResponsiveButton from "../../ui/omniResponsiveButton.js";
import Widget from "../../ui/widget.js";
import WidgetList from "../../ui/widgetList.js";
import WidgetSkeleton from "../../ui/widgetSkeleton.js";
import WidgetTitle from "../../ui/widgetTitle.js";

const statistical = require("statistical-js");

const NsightPatientSymptoms = props => {
  const { patientChart, title = "Health", allowAdd = true } = props;

  const theme = useTheme();
  const mobileLayout = useMediaQuery(theme.breakpoints.down("sm"));

  const [showInputDialog, setShowInputDialog] = useState(false);

  const [singleSymptomData, setSingleSymptomData] = useState(null);

  const generateHistoryCounts = () => {
    const userInfo = patientChart;
    if (userInfo == null) {
      return [];
    }
    return new Array(Object.entries(userInfo.symptoms).length).fill(1);
  };

  const [historyCount, setHistoryCount] = useState(generateHistoryCounts());

  const generateSymptomData = () => {
    const userInfo = patientChart;
    if (userInfo == null) {
      return null;
    }

    const singleSymptomsData = [];
    for (const [key] of Object.entries(userInfo.symptoms)) {
      let graphData = extractGraphDataFromArray(userInfo.symptoms[key].data);

      let linearInput = [];
      for (let x = 0; x < graphData.values.length; ++x) {
        linearInput.push([x, graphData.values[x]]);
      }
      //this takes an array [x,y] values
      const linearOutput = statistical.methods.linearRegression(linearInput);

      const average = statistical.methods.mean(graphData.values);

      singleSymptomsData.push({
        name: capitalizeAllWords(key.replace(/([A-Z])/g, " $1").trim()),
        averageSeverity: Math.round(average),
        regressionValue: linearOutput,
        graphDataComplete: graphData.values,
        graphLabels: graphData.labels,
        graphIndex: 0,
        symptomId: key,
      });
    }

    singleSymptomsData.sort((a, b) => b.averageSeverity - a.averageSeverity);

    //set the Fake editing data

    return singleSymptomsData;
  };

  const getRegressionDescription = regressionValue => {
    const toFixed = regressionValue.slope.toFixed(2);
    if (toFixed > 0.01) {
      return "Getting Worse";
    }
    if (toFixed < -0.01) {
      return "Getting Better";
    } else {
      return "Steady";
    }
  };

  const getRegressionIcon = regressionValue => {
    const toFixed = regressionValue.slope.toFixed(2);
    if (toFixed > 0.01) {
      return (
        <TrendingUp
          sx={{
            color: theme.palette.commonground.contrastText,
            width: "18px",
            height: "18px",
            marginLeft: "2px",
            marginTop: "2px",
            fontSize: "1.0rem",
          }}
        />
      );
    }
    if (toFixed < -0.01) {
      return (
        <TrendingDown
          sx={{
            color: theme.palette.commonground.contrastText,
            width: "18px",
            height: "18px",
            marginLeft: "2px",
            marginTop: "2px",
            fontSize: "1.0rem",
          }}
        />
      );
    } else {
      return (
        <TrendingFlat
          sx={{
            color: theme.palette.commonground.contrastText,
            width: "18px",
            height: "18px",
            marginLeft: "2px",
            marginTop: "2px",
            fontSize: "1.0rem",
          }}
        />
      );
    }
  };

  const [symptomData, setSymptomData] = useState(generateSymptomData());

  useEffect(() => {
    setSymptomData(generateSymptomData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientChart]);

  const getPatientHistory = (symptom, index) => {
    let localHistoryCount = historyCount[index];
    if (isNaN(localHistoryCount)) {
      localHistoryCount = 1;
    }
    const values = symptom.graphDataComplete.slice(
      Math.max(symptom.graphDataComplete.length - localHistoryCount, 0),
    );
    const labels = symptom.graphLabels.slice(
      Math.max(symptom.graphLabels.length - localHistoryCount, 0),
    );

    return (
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "row",
          color: alpha(theme.palette.commonground.contrastText, 0.55),
          textAlign: "right",
        }}
      >
        <IconButton
          sx={[
            {
              marginTop: theme.spacing(0.5),
              marginBottom: theme.spacing(0.5),
              paddingLeft: "2px",
              paddingRight: "2px",
              maxWidth: "24px",
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: alpha(theme.palette.commonground.contrastText, 0.25),
              },
            },
            localHistoryCount === 1 && {
              marginRight: `-${theme.spacing(1)}`,
            },
          ]}
          onClick={e => {
            e.stopPropagation();
            const newHist = historyCount.slice();
            if (localHistoryCount === 1) {
              newHist[index] = mobileLayout ? 2 : 4;
            } else {
              newHist[index] = 1;
            }
            setHistoryCount(newHist);
          }}
          size="large"
        >
          {localHistoryCount === 1 ? <BackIcon /> : <NextIcon />}
        </IconButton>

        <Box
          sx={{
            textAlign: "center",
            position: "relative",
            display: "flex",
            flexDirection: "row",
            color: alpha(theme.palette.commonground.contrastText, 0.55),
          }}
        >
          {values.map((value, index) => {
            const theDate = OmniStringToDate(labels[index]);
            return (
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  textAlign: "center",
                  width: "58px",
                  height: "42px",
                  alignItems: "center",
                }}
                key={"value" + symptom.name + index}
              >
                <Typography variant="overline" sx={{ marginTop: "-4px" }}>
                  {localHistoryCount === 1
                    ? "Last"
                    : theDate.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                </Typography>
                <OmniAvatar
                  sx={[
                    {
                      width: 28,
                      height: 28,
                      fontSize: "1.2rem",
                      marginTop: `-${theme.spacing(1)}`,
                      alignItems: "center",
                    },
                    value >= 7 && {
                      color: darken(theme.palette.error.main, 0.5),
                      backgroundColor: lighten(theme.palette.error.main, 0.5),
                      border: `2px solid ${darken(theme.palette.error.main, 0.5)}`,
                    },
                    value > 3 &&
                      value < 7 && {
                        color: darken(theme.palette.warning.main, 0.5),
                        backgroundColor: lighten(theme.palette.warning.main, 0.5),
                        border: `2px solid ${darken(theme.palette.warning.main, 0.5)}`,
                      },
                    value <= 3 && {
                      color: darken(theme.palette.success.main, 0.5),
                      backgroundColor: lighten(theme.palette.success.main, 0.5),
                      border: `2px solid ${darken(theme.palette.success.main, 0.5)}`,
                    },
                  ]}
                >
                  {value}
                </OmniAvatar>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const [titleText, setTitleText] = useState(null);

  useEffect(() => {
    if (symptomData != null) {
      setTitleText(`Tracking ${symptomData.length} Symptom${symptomData.length === 1 ? "" : "s"}`);
    } else setTitleText("");
  }, [symptomData]);

  return (
    <Widget>
      <WidgetTitle title={title} text={titleText} loading={symptomData == null}>
        {allowAdd && (
          <OmniResponsiveButton
            tooltipTitle="Update Symptoms"
            buttonText="Update"
            icon={<EditIcon />}
            onClick={() => {
              setShowInputDialog(true);
            }}
          />
        )}
      </WidgetTitle>

      {patientChart == null && <WidgetSkeleton />}
      {patientChart != null && (
        <WidgetList>
          {symptomData != null &&
            symptomData.map((symptom, index) => {
              return (
                <ListItem
                  dense
                  key={index}
                  disableGutters={true}
                  onClick={() => {
                    setSingleSymptomData(symptom);
                  }}
                  sx={[
                    {
                      padding: theme.spacing(1),
                      paddingTop: 0,
                      paddingBottom: 0,
                      marginBottom: theme.spacing(1),
                      borderRadius: theme.spacing(1),
                      boxShadow: theme.shadows[3],
                    },
                    symptom.averageSeverity >= 7 && {
                      backgroundColor: alpha(theme.palette.error.main, 0.75),
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.error.main, 0.5),
                        cursor: "pointer",
                      },
                    },
                    symptom.averageSeverity > 3 &&
                      symptom.averageSeverity < 7 && {
                        backgroundColor: alpha(theme.palette.warning.main, 0.75),
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.warning.main, 0.5),
                          cursor: "pointer",
                        },
                      },
                    symptom.averageSeverity <= 3 && {
                      backgroundColor: alpha(theme.palette.success.main, 0.75),
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.success.main, 0.5),
                        cursor: "pointer",
                      },
                    },
                  ]}
                >
                  <Tooltip title="Average Symptom Severity" placement="top">
                    <OmniAvatar
                      sx={[
                        {
                          width: 42,
                          height: 42,
                          fontSize: "1.8rem",
                          boxShadow: theme.shadows[3],
                        },
                        symptom.averageSeverity >= 7 && {
                          color: darken(theme.palette.error.main, 0.5),
                          backgroundColor: lighten(theme.palette.error.main, 0.5),
                          border: `2px solid ${darken(theme.palette.error.main, 0.5)}`,
                        },
                        symptom.averageSeverity > 3 &&
                          symptom.averageSeverity < 7 && {
                            color: darken(theme.palette.warning.main, 0.5),
                            backgroundColor: lighten(theme.palette.warning.main, 0.5),
                            border: `2px solid ${darken(theme.palette.warning.main, 0.5)}`,
                          },
                        symptom.averageSeverity <= 3 && {
                          color: darken(theme.palette.success.main, 0.5),
                          backgroundColor: lighten(theme.palette.success.main, 0.5),
                          border: `2px solid ${darken(theme.palette.success.main, 0.5)}`,
                        },
                      ]}
                    >
                      {symptom.averageSeverity}
                    </OmniAvatar>
                  </Tooltip>
                  <ListItemText
                    primary={
                      <Typography
                        component="span"
                        variant="h6"
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          marginLeft: theme.spacing(1),
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          marginTop: 0,
                          color: theme.palette.commonground.contrastText,
                          webkitUserSelect: "none",
                          userSelect: "none",
                        }}
                      >
                        {symptom.name}
                      </Typography>
                    }
                    secondary={
                      <Fragment>
                        <Typography
                          component="span"
                          variant="subtitle2"
                          color="textPrimary"
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            marginLeft: theme.spacing(1),
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            marginTop: "-6px",
                            color: theme.palette.commonground.contrastText,
                            webkitUserSelect: "none",
                            userSelect: "none",
                          }}
                        >
                          {getRegressionDescription(symptom.regressionValue)}
                          {getRegressionIcon(symptom.regressionValue)}
                        </Typography>
                      </Fragment>
                    }
                  />

                  {getPatientHistory(symptom, index)}
                </ListItem>
              );
            })}
        </WidgetList>
      )}

      {showInputDialog && (
        <PatientSymptomsDialog
          open={symptomData != null}
          symptomData={symptomData}
          onAccept={updatedSymptoms => {
            //update the PatientChart with the new data
            let today = new Date();
            today = today.toISOString().substring(0, 10);
            let newSymptoms = deepCopyObject(patientChart.symptoms);

            for (let index = 0; index < updatedSymptoms.length; ++index) {
              let theNewEntry = {
                date: today,
                value: updatedSymptoms[index].newReportedValue,
                note: "",
              };
              newSymptoms[updatedSymptoms[index].symptomId].data.push(theNewEntry);
            }

            props.updatePatientSymptomsRequest(newSymptoms);
            setShowInputDialog(false);
          }}
          onClose={() => {
            setShowInputDialog(false);
          }}
        />
      )}

      {singleSymptomData != null && (
        <SingleSymptomDialog
          open={singleSymptomData != null}
          onAccept={() => {
            setSingleSymptomData(null);
          }}
          onClose={() => {
            setSingleSymptomData(null);
          }}
          dialogTitle={`${singleSymptomData.name} History`}
          singleSymptomData={singleSymptomData}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  patientODM: state.patientSession.patientODM,
  patientChart: state.patientSession.patientChart,
});

const mapDispatchToProps = {
  updatePatientSymptomsRequest: PatientSessionActions.updatePatientSymptomsRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(NsightPatientSymptoms);
